import React, { useMemo } from "react";
import { commonMonoClineStyles, useTheme } from "@me8eon/themes";
import { MonoClineProps, MonoClineTc } from "@me8eon/components";
import { useLanguage } from "@me8eon/language";
import { langBasedCapitalizeFirst } from "@me8eon/string_utils";

export type GroupResult<Item> = Record<string, Item[]>;

export function groupMonocline<Item>(
    items: Item[],
    tc: MonoClineTc<Item>,
): GroupResult<Item> {
    const result: GroupResult<Item> = {};

    for (const item of items || []) {
        const category = tc.categorise(item);
        const group = result[category] || [];
        group.push(item);
        result[category] = group;
    }

    return result;
}

/**
 * SimpleMonoCline: lays out a "monocline" of headers, each with its corresponding data items.
 * Uses CSS grid to distribute sections evenly.
 */
export const SimpleMonoCline = <I,>({
    items,
    typeClass,
    Header: HeaderComponent,
    Data: DataComponent,
    selectedItem,
    onDataClick,
    onCategoryClick,
    disclaimer,
    isMobile,
}: MonoClineProps<I>): JSX.Element => {
    // Group items by category
    const grouped = useMemo(
        () => groupMonocline(items, typeClass),
        [items, typeClass],
    );
    const [_language] = useLanguage()

    // Only include categories that contain at least one item
    const headers = useMemo(
        () => Object.keys(grouped).filter((category) => (grouped[category] || []).length > 0),
        [grouped],
    );

    // Calculate dynamic columns based on number of non-empty categories
    const columnTemplate = `repeat(${headers.length}, minmax(0, 1fr))`;
    const { monoClineStyles = commonMonoClineStyles } = useTheme();

    return (
        <>
            <div
                data-testid="monocline-container"
                style={{
                    ...monoClineStyles.container,
                    border: "8px solid #f8f5f2",
                    gridTemplateColumns: columnTemplate,
                }}
            >
                {headers.map((category, catIndex) => {
                    const groupItems = grouped[category] || [];
                    const selectedCategory = selectedItem && groupItems.find(g => g === selectedItem) !== undefined;
                    return (
                        <section key={category} style={monoClineStyles.section}>
                            {/* Render header using first item in the group */}
                            <HeaderComponent isMobile={isMobile} category={langBasedCapitalizeFirst(category, _language)} index={catIndex} onClick={onCategoryClick} selected={selectedCategory} />

                            <div style={monoClineStyles.dataWrapper}>
                                {/* Render each data item */}
                                {groupItems.map((item, idx) => (
                                    <DataComponent isMobile={isMobile} item={item} key={idx} category={category} index={idx} onClick={onDataClick} selected={item === selectedItem} />
                                ))}
                            </div>
                        </section>
                    );
                })}
            </div>
            {disclaimer && (
                <div style={{
                    position: "sticky",
                    bottom: "0",
                    padding: `0.5rem ${isMobile ? "8px" : "14px"}`,
                    background: "#fff",
                    borderTop: "1px solid #f9f5f5",
                }}>
                    {disclaimer}
                </div>
            )}
        </>
    );
};